import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { assistanceService } from '@/services/assistance'
import type { AssistanceFilters } from '@/types/assistance'
import { useAuth } from '@/contexts/AuthContext'

// ============================================
// ASSISTANCE REQUESTS HOOKS
// ============================================

export interface UseAssistanceRequestsParams extends AssistanceFilters {
  page?: number
  limit?: number
}

export function useAssistanceRequests(params: UseAssistanceRequestsParams = {}) {
  const { user } = useAuth()
  const { page = 1, limit = 20, ...filters } = params

  return useQuery({
    queryKey: ['assistance-requests', params],
    queryFn: () => assistanceService.getAll(filters, page, limit),
    enabled: !!user,
  })
}

export function useAssistanceRequest(id?: string) {
  const { user } = useAuth()

  return useQuery({
    queryKey: ['assistance-request', id],
    queryFn: () => assistanceService.getById(id!),
    enabled: !!user && !!id,
  })
}

export function useProjectAssistanceRequests(projectId?: string) {
  const { user } = useAuth()

  return useQuery({
    queryKey: ['assistance-requests', 'project', projectId],
    queryFn: () => assistanceService.getByProjectId(projectId!),
    enabled: !!user && !!projectId,
  })
}

export function useAssistanceStats() {
  const { user } = useAuth()

  return useQuery({
    queryKey: ['assistance-requests', 'stats'],
    queryFn: () => assistanceService.getStats(),
    enabled: !!user,
  })
}

export function useAssistanceSubmittedCount() {
  const { user } = useAuth()

  return useQuery({
    queryKey: ['assistance-requests', 'submitted-count'],
    queryFn: () => assistanceService.getSubmittedCount(),
    enabled: !!user,
    refetchInterval: 30000,
  })
}

export function useUpdateAssistanceRequest() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: Record<string, any> }) =>
      assistanceService.update(id, updates),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['assistance-requests'] })
      queryClient.invalidateQueries({ queryKey: ['assistance-request', data.id] })
    },
  })
}

// ============================================
// CONTRACTS HOOKS
// ============================================

export function useProjectContract(projectId?: string) {
  const { user } = useAuth()

  return useQuery({
    queryKey: ['contract', 'project', projectId],
    queryFn: () => assistanceService.getContractByProjectId(projectId!),
    enabled: !!user && !!projectId,
  })
}

export function useCreateContract() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: Record<string, any>) => assistanceService.createContract(data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['contract'] })
      if (data.project_id) {
        queryClient.invalidateQueries({ queryKey: ['contract', 'project', data.project_id] })
      }
    },
  })
}

export function useUpdateContract() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: Record<string, any> }) =>
      assistanceService.updateContract(id, updates),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['contract'] })
      if (data.project_id) {
        queryClient.invalidateQueries({ queryKey: ['contract', 'project', data.project_id] })
      }
    },
  })
}

// ============================================
// CONTRACT COVERAGES HOOKS
// ============================================

export function useContractCoverages(contractId?: string) {
  const { user } = useAuth()

  return useQuery({
    queryKey: ['contract-coverages', contractId],
    queryFn: () => assistanceService.getCoverages(contractId!),
    enabled: !!user && !!contractId,
  })
}

export function useUpsertCoverage() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: Parameters<typeof assistanceService.upsertCoverage>[0]) =>
      assistanceService.upsertCoverage(data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['contract-coverages', data.contract_id] })
      queryClient.invalidateQueries({ queryKey: ['contract'] })
    },
  })
}

export function useDeleteCoverage() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, contractId: _contractId }: { id: string; contractId: string }) =>
      assistanceService.deleteCoverage(id),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['contract-coverages', variables.contractId] })
      queryClient.invalidateQueries({ queryKey: ['contract'] })
    },
  })
}

export function useInitializeDefaultCoverages() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (contractId: string) => assistanceService.initializeDefaultCoverages(contractId),
    onSuccess: (data) => {
      if (data.length > 0) {
        queryClient.invalidateQueries({ queryKey: ['contract-coverages', data[0].contract_id] })
        queryClient.invalidateQueries({ queryKey: ['contract'] })
      }
    },
  })
}
